import { defineStore } from 'pinia';
import { Store } from '@/setup/globals';
import { setInitialData } from '@/stores/plugins/initial-data';
import useSessionStore from '@/stores/session';
import api, { uniqueGet } from '@/helpers/api';
import getShopSearchLink from '@/helpers/get-shop-search-link';
import mapCommerceSearchResults from '@/helpers/map-commerce-search-results';

export type StoreState = {
  quickLinks: Record<string, unknown>[];
  searchSuggestionsPimcore: Record<string, unknown>[];
  searchSuggestionsCommerce: Record<string, unknown>[];
  results: Record<string, unknown>[];
  query: string;
  index: string | null;
  indices: Record<string, unknown>[];
  filters: Record<string, unknown>[];
  pagination: {
    totalAmount: number;
    currentPage: number;
    itemsPerPage: number;
  };
  commerceSearchInformation: Record<string, unknown> | null;
  runningRequests: {
    apiFetchSearchSuggestions: boolean;
    submitSearchShop: boolean;
    submitSearchWebsite: boolean;
    apiFetchSearchResults: boolean;
  };
};

const storeName = Store.Search;

export default defineStore(storeName, {
  state: () => {
    const initialData: Partial<StoreState> = window.initialData?.[storeName] || {};
    const state: StoreState = {
      quickLinks: [],
      searchSuggestionsPimcore: [],
      searchSuggestionsCommerce: [],
      results: [],
      query: '',
      index: null,
      indices: [],
      filters: [],
      pagination: {
        totalAmount: 0,
        currentPage: 0,
        itemsPerPage: 12,
      },
      commerceSearchInformation: null,
      runningRequests: {
        apiFetchSearchSuggestions: false,
        submitSearchShop: false,
        submitSearchWebsite: false,
        apiFetchSearchResults: false,
      },
    };

    setInitialData(state, initialData);

    return state;
  },
  getters: {
    getItemsPerPage: (state) => state.pagination.itemsPerPage,
    getCurrentPage: (state) => state.pagination.currentPage || 1,
    getTotalAmount: (state) => state.pagination.totalAmount || 0,
    getCommerceProductCount: (state) => {
      const { count } = state.commerceSearchInformation || {};

      if (Number.isNaN(count)) {
        return null;
      }

      return count;
    },
  },
  actions: {
    /**
     * Fetches search results from server.
     */
    apiFetchResults({ index, page, limit, reset, filters }) {
      this.runningRequests.apiFetchSearchResults = true;

      const url = api.getUrl('search-results');
      const { query } = this;
      const payload = {
        page,
        limit,
        index,
        query,
        filters,
      };

      return api
        .post(url, payload)
        .then((response) => {
          const { pagination, entries, index: esIndex, indices, filters: responseFilters } = response.data?.data || {};

          if (esIndex && pagination && entries) {
            if (reset) {
              this.results = [];
            }

            this.index = esIndex;

            if (pagination.totalAmount) {
              this.pagination.totalAmount = pagination.totalAmount;
            }

            if (pagination.currentPage) {
              this.pagination.currentPage = pagination.currentPage;
            }

            if (pagination.itemsPerPage) {
              this.pagination.itemsPerPage = pagination.itemsPerPage;
            }

            if (entries?.length) {
              entries.forEach((entry) => {
                this.results.push({ ...entry, page: pagination.currentPage });
              });
            }

            if (responseFilters?.length) {
              this.filters = responseFilters;
            }

            if (indices?.length) {
              this.indices = indices;
            }

            return response;
          }

          throw new Error('API failure during "search/apiFetchSearchResults" request.');
        })
        .finally(() => {
          this.runningRequests.apiFetchSearchResults = false;
        });
    },

    /**
     * Fetches the search combined (pimcore and commerce) search suggestions.
     */
    apiFetchSearchSuggestions({ term, amount = 10 }) {
      this.runningRequests.apiFetchSearchResults = true;
      this.searchSuggestionsPimcore = [];
      this.searchSuggestionsCommerce = [];

      return Promise.all([
        this.apiFetchSearchSuggestionsPimcore({ term, amount }),
        this.apiFetchSearchSuggestionsCommerce({ term, amount }),
      ]).finally(() => {
        this.runningRequests.apiFetchSearchResults = false;
      });
    },

    /**
     * Fetches the search suggestions from pimcore.
     */
    apiFetchSearchSuggestionsPimcore({ term, amount = 10 }) {
      const url = api.getUrl('search-suggestions');
      const config = {
        params: {
          searchstring: term,
          results: amount,
        },
      };

      return uniqueGet(url, 'apiFetchSearchSuggestionsPimcore', config).then((response) => {
        const { data } = response.data || {};

        if (response.isCancelled) {
          // We don't want to log an error in this case.
          return null;
        }

        if (Array.isArray(data)) {
          this.searchSuggestionsPimcore = data;

          return response;
        }

        throw new Error('API failure during "search/apiFetchSearchSuggestionsPimcore" request.');
      });
    },

    /**
     * Fetches the search suggestions from commerce.
     */
    apiFetchSearchSuggestionsCommerce({ term, amount = 10 }) {
      if (!useSessionStore().isCartEnabled) {
        return Promise.resolve(null);
      }

      const url = api.getUrl('shop-search-suggestions');
      const data = {
        params: {
          term,
          resultSize: amount,
        },
      };
      const notificationConfiguration = {
        preventNotifications: true,
      };

      return uniqueGet(url, 'apiFetchSearchSuggestionsCommerce', data, notificationConfiguration).then((response) => {
        const { products } = response.data || {};

        if (response.isCancelled) {
          // We don't want to log an error in this case.
          return null;
        }

        if (Array.isArray(products)) {
          this.searchSuggestionsCommerce = mapCommerceSearchResults(products);

          return response;
        }

        throw new Error('API failure during "search/apiFetchSearchSuggestionsCommerce" request.');
      });
    },

    /**
     * Fetches search information from commerce.
     */
    apiFetchSearchInformationCommerce() {
      if (!useSessionStore().isCartEnabled) {
        return Promise.resolve(null);
      }

      const url = api.getUrl('shop-search-facets');
      const config = {
        params: { q: this.query }, // eslint-disable-line id-length
      };
      const notificationConfiguration = {
        preventNotifications: true,
      };

      return api.get(url, config, notificationConfiguration).then((response) => {
        const { data } = response;

        if (data) {
          this.commerceSearchInformation = Object.freeze(data);

          return response;
        }

        throw new Error('API failure during "search/apiFetchSearchInformationCommerce" request.');
      });
    },

    /**
     * Submits the shop search.
     */
    submitSearchShop({ query }) {
      if (query) {
        this.runningRequests.submitSearchShop = true;

        window.location.href = getShopSearchLink(query);
      }
    },

    /**
     * Submits the website search.
     */
    submitSearchWebsite({ query }) {
      if (query) {
        this.runningRequests.submitSearchWebsite = true;

        window.location.href = `${useSessionStore().urls.searchResultPage}?query=${query}`;
      }
    },
  },
});
